import React from 'react';
import { AdminSettings, UserAccount } from '../../types';
import { userService } from '../../services/userService';

type Props = {
  settings: AdminSettings;
  setSettings: (s: AdminSettings) => void;
  saveSettings: (s: AdminSettings) => Promise<void> | void;
};

type SPhoneSettings = NonNullable<AdminSettings['sPhone']>;
type SPhoneAccount = SPhoneSettings['accounts'][number];

const defaultSPhone = (): SPhoneSettings => ({
  enabled: false,
  wsServer: '',
  domain: '',
  autoRegister: true,
  defaultOutboundPrefix: '',
  iceServers: ['stun:stun.l.google.com:19302'],
  accounts: [],
});

const toSPhoneSettings = (settings: AdminSettings): SPhoneSettings => {
  const raw = settings.sPhone;
  return {
    ...defaultSPhone(),
    ...(raw || {}),
    accounts: Array.isArray(raw?.accounts) ? raw!.accounts : [],
  };
};

export const SPhoneTab: React.FC<Props> = ({ settings, setSettings, saveSettings }) => {
  const [users, setUsers] = React.useState<UserAccount[]>([]);
  const [loadingUsers, setLoadingUsers] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [draft, setDraft] = React.useState<SPhoneSettings>(() => toSPhoneSettings(settings));

  React.useEffect(() => {
    setDraft(toSPhoneSettings(settings));
  }, [settings]);

  React.useEffect(() => {
    let mounted = true;
    setLoadingUsers(true);
    userService
      .getAllUsers()
      .then((list) => {
        if (!mounted) return;
        setUsers(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!mounted) return;
        setUsers([]);
      })
      .finally(() => {
        if (!mounted) return;
        setLoadingUsers(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const accountsByUser = React.useMemo(() => {
    const map = new Map<string, SPhoneAccount>();
    for (const account of draft.accounts || []) {
      const id = String(account?.userId || '').trim();
      if (!id) continue;
      map.set(id, account);
    }
    return map;
  }, [draft.accounts]);

  const updateAccount = (userId: string, patch: Partial<SPhoneAccount>) => {
    setDraft((prev) => {
      const existing = prev.accounts.find((item) => item.userId === userId);
      const nextEntry: SPhoneAccount = {
        userId,
        enabled: existing?.enabled ?? false,
        username: existing?.username || '',
        password: existing?.password || '',
        extension: existing?.extension || '',
        displayName: existing?.displayName || '',
        allowInternalCalls: existing?.allowInternalCalls ?? true,
        allowExternalCalls: existing?.allowExternalCalls ?? true,
        ...patch,
      };
      const withoutCurrent = prev.accounts.filter((item) => item.userId !== userId);
      return {
        ...prev,
        accounts: [...withoutCurrent, nextEntry],
      };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const normalized: SPhoneSettings = {
        ...draft,
        wsServer: String(draft.wsServer || '').trim(),
        domain: String(draft.domain || '').trim(),
        defaultOutboundPrefix: String(draft.defaultOutboundPrefix || '').trim(),
        iceServers: String((draft.iceServers || []).join('\n'))
          .split(/\r?\n/)
          .map((item) => item.trim())
          .filter(Boolean),
        accounts: (draft.accounts || []).map((account) => ({
          ...account,
          userId: String(account.userId || '').trim(),
          username: String(account.username || '').trim(),
          password: String(account.password || ''),
          extension: String(account.extension || '').trim(),
          displayName: String(account.displayName || '').trim(),
          allowInternalCalls: account.allowInternalCalls !== false,
          allowExternalCalls: account.allowExternalCalls !== false,
        })),
      };
      const next = { ...settings, sPhone: normalized };
      setSettings(next);
      await saveSettings(next);
    } finally {
      setSaving(false);
    }
  };

  const activeUsers = users.filter((user) => String(user.status || '').toUpperCase() !== 'DISABLED');

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-black text-slate-900">S.PHONE</h3>
          <p className="text-xs text-slate-500">Per-operator SIP accounts and PBX browser softphone settings.</p>
        </div>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="rounded-xl border border-indigo-300 bg-indigo-600 px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-white disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save S.PHONE'}
        </button>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <input
            type="checkbox"
            checked={!!draft.enabled}
            onChange={(event) => setDraft((prev) => ({ ...prev, enabled: event.target.checked }))}
          />
          Enable Softphone Platform
        </label>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <input
            type="checkbox"
            checked={draft.autoRegister !== false}
            onChange={(event) => setDraft((prev) => ({ ...prev, autoRegister: event.target.checked }))}
          />
          Auto Register On Open
        </label>
        <label className="flex flex-col gap-1 text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
          PBX WebSocket (WSS)
          <input
            type="text"
            value={draft.wsServer}
            onChange={(event) => setDraft((prev) => ({ ...prev, wsServer: event.target.value }))}
            placeholder="wss://pbx.example.com:7443/ws"
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
          SIP Domain
          <input
            type="text"
            value={draft.domain}
            onChange={(event) => setDraft((prev) => ({ ...prev, domain: event.target.value }))}
            placeholder="pbx.example.com"
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-bold uppercase tracking-[0.12em] text-slate-500 md:col-span-2">
          Default Outbound Prefix
          <input
            type="text"
            value={draft.defaultOutboundPrefix || ''}
            onChange={(event) => setDraft((prev) => ({ ...prev, defaultOutboundPrefix: event.target.value }))}
            placeholder="9"
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-bold uppercase tracking-[0.12em] text-slate-500 md:col-span-2">
          ICE Servers (one per line)
          <textarea
            rows={3}
            value={(draft.iceServers || []).join('\n')}
            onChange={(event) =>
              setDraft((prev) => ({
                ...prev,
                iceServers: String(event.target.value || '')
                  .split(/\r?\n/)
                  .map((item) => item.trim())
                  .filter(Boolean),
              }))
            }
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"
          />
        </label>
      </div>

      <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200">
        <table className="min-w-[1120px] w-full text-sm">
          <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Operator</th>
              <th className="px-3 py-2 text-left">Enabled</th>
              <th className="px-3 py-2 text-left">Ext</th>
              <th className="px-3 py-2 text-left">Username</th>
              <th className="px-3 py-2 text-left">Password</th>
              <th className="px-3 py-2 text-left">Display Name</th>
              <th className="px-3 py-2 text-left">Internal</th>
              <th className="px-3 py-2 text-left">External</th>
              <th className="px-3 py-2 text-left">State</th>
            </tr>
          </thead>
          <tbody>
            {loadingUsers ? (
              <tr>
                <td className="px-3 py-3 text-slate-500" colSpan={9}>Loading operators...</td>
              </tr>
            ) : activeUsers.length === 0 ? (
              <tr>
                <td className="px-3 py-3 text-slate-500" colSpan={9}>No operators found.</td>
              </tr>
            ) : activeUsers.map((user) => {
              const account = accountsByUser.get(String(user.id)) || null;
              const hasCreds = !!String(account?.username || '').trim() && !!String(account?.password || '').trim();
              const configured = !!account?.enabled && hasCreds;
              return (
                <tr key={user.id} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    <div className="font-bold text-slate-800">{user.name || user.email || user.id}</div>
                    <div className="text-xs text-slate-500">{user.role}</div>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={!!account?.enabled}
                      onChange={(event) => updateAccount(String(user.id), { enabled: event.target.checked })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={String(account?.extension || '')}
                      onChange={(event) => updateAccount(String(user.id), { extension: event.target.value })}
                      className="w-24 rounded-lg border border-slate-300 px-2 py-1 text-sm"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={String(account?.username || '')}
                      onChange={(event) => updateAccount(String(user.id), { username: event.target.value })}
                      className="w-40 rounded-lg border border-slate-300 px-2 py-1 text-sm"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="password"
                      value={String(account?.password || '')}
                      onChange={(event) => updateAccount(String(user.id), { password: event.target.value })}
                      placeholder="***"
                      className="w-40 rounded-lg border border-slate-300 px-2 py-1 text-sm"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={String(account?.displayName || '')}
                      onChange={(event) => updateAccount(String(user.id), { displayName: event.target.value })}
                      className="w-44 rounded-lg border border-slate-300 px-2 py-1 text-sm"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={account?.allowInternalCalls !== false}
                      onChange={(event) => updateAccount(String(user.id), { allowInternalCalls: event.target.checked })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={account?.allowExternalCalls !== false}
                      onChange={(event) => updateAccount(String(user.id), { allowExternalCalls: event.target.checked })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex rounded-full px-2 py-1 text-[10px] font-black uppercase tracking-[0.1em] ${configured ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {configured ? 'Configured' : 'Needs Setup'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default SPhoneTab;
